import { supabase } from '../lib/supabase';

export const publicationsService = {
	// Get all publications (for admin) or published only (for public)
	async getPublications(adminMode = false) {
		try {
			let query = supabase
				.from('publications')
				.select('*')
				.order('publication_date', { ascending: false });

			// Only filter by published status if not in admin mode
			if (!adminMode) {
				query = query.eq('is_published', true);
			}

			const { data, error } = await query;

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error('Error fetching publications:', error);
			return { data: null, error };
		}
	},

	// Get featured publications
	async getFeaturedPublications() {
		try {
			const { data, error } = await supabase
				.from('publications')
				.select('*')
				.eq('is_published', true)
				.eq('is_featured', true)
				.order('publication_date', { ascending: false });

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error('Error fetching featured publications:', error);
			return { data: null, error };
		}
	},

	// Get publications by type
	async getPublicationsByType(type) {
		try {
			const { data, error } = await supabase
				.from('publications')
				.select('*')
				.eq('is_published', true)
				.eq('publication_type', type)
				.order('publication_date', { ascending: false });

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error('Error fetching publications by type:', error);
			return { data: null, error };
		}
	},

	// Get publications by author
	async getPublicationsByAuthor(authorId) {
		try {
			const { data, error } = await supabase
				.from('publications')
				.select('*')
				.eq('is_published', true)
				.eq('author_id', authorId)
				.order('publication_date', { ascending: false });

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error('Error fetching publications by author:', error);
			return { data: null, error };
		}
	},

	// Get a specific publication by slug
	async getPublicationBySlug(slug) {
		try {
			const { data, error } = await supabase
				.from('publications')
				.select('*')
				.eq('slug', slug)
				.eq('is_published', true)
				.single();

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error('Error fetching publication by slug:', error);
			return { data: null, error };
		}
	},

	// Increment view count (RPC: anon cannot UPDATE rows under strict RLS)
	async incrementViewCount(slug) {
		try {
			const { error } = await supabase.rpc('increment_publication_view_count', {
				p_slug: slug,
			});

			if (error) throw error;
			return { error: null };
		} catch (error) {
			console.error('Error incrementing view count:', error);
			return { error };
		}
	},

	// Create a new publication
	async createPublication(publicationData) {
		try {
			const { data, error } = await supabase
				.from('publications')
				.insert([publicationData])
				.select()
				.single();

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error('Error creating publication:', error);
			return { data: null, error };
		}
	},

	// Add external URL to publication
	async addExternalUrl(id, externalUrl) {
		try {
			const { data, error } = await supabase
				.from('publications')
				.update({ external_url: externalUrl })
				.eq('id', id)
				.select()
				.single();

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error('Error adding external URL:', error);
			return { data: null, error };
		}
	},

	// Update a publication
	async updatePublication(id, updates) {
		try {
			const { data, error } = await supabase
				.from('publications')
				.update(updates)
				.eq('id', id)
				.select()
				.single();

			if (error) throw error;
			return { data, error: null };
		} catch (error) {
			console.error('Error updating publication:', error);
			return { data: null, error };
		}
	},

	// Delete a publication
	async deletePublication(id) {
		try {
			const { error } = await supabase
				.from('publications')
				.delete()
				.eq('id', id);

			if (error) throw error;
			return { error: null };
		} catch (error) {
			console.error('Error deleting publication:', error);
			return { error };
		}
	}
};